import { useMemo, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Badge } from "@project/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@project/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { Checkbox } from "@project/components/ui/checkbox";
import { cn } from "@project/lib/utils";
import type {
  ProductVariant,
  ProductVariantCreateItem,
  ProductVariantMaster,
} from "@project/types/gruh-udhyog";

export type ProductOptionRow = {
  key: string;
  masterId: number | null;
  title: string;
  availableValues: string[];
  selectedValues: string[];
};

export type VariantFormRow = {
  key: string;
  value: string;
  title: string;
  sku: string;
  /** SKU from API when editing — used to omit unchanged sku on update */
  originalSku?: string;
  price: string;
  discounted_price: string;
  tax: string;
  total_stock: string;
  is_active: boolean;
};

type ProductFormVariantsProps = {
  variantMasters: ProductVariantMaster[];
  options: ProductOptionRow[];
  onOptionsChange: (options: ProductOptionRow[]) => void;
  variants: VariantFormRow[];
  onVariantsChange: (variants: VariantFormRow[]) => void;
};

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function cartesian(groups: string[][]): string[][] {
  if (groups.length === 0) {
    return [];
  }
  return groups.reduce<string[][]>(
    (acc, group) =>
      acc.length === 0
        ? group.map(v => [v])
        : acc.flatMap(prefix => group.map(v => [...prefix, v])),
    []
  );
}

function formatMoney(value: string | number | undefined | null) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "₹0.00";
  }
  return `₹${n.toFixed(2)}`;
}

function resolveDisplayPriceAmount(row: VariantFormRow): string {
  const discounted = Number(row.discounted_price);
  if (Number.isFinite(discounted) && discounted > 0) {
    return discounted.toFixed(2);
  }
  return Number(row.price || 0).toFixed(2);
}

function displayPrice(row: VariantFormRow) {
  return formatMoney(resolveDisplayPriceAmount(row));
}

export function emptyVariantRow(value = ""): VariantFormRow {
  return {
    key: newKey(),
    value,
    title: value,
    sku: "",
    price: "",
    discounted_price: "",
    tax: "",
    total_stock: "",
    is_active: true,
  };
}

export function emptyOptionRow(): ProductOptionRow {
  return {
    key: newKey(),
    masterId: null,
    title: "",
    availableValues: [],
    selectedValues: [],
  };
}

export function buildVariantsFromOptions(
  options: ProductOptionRow[],
  previous: VariantFormRow[]
): VariantFormRow[] {
  const valueGroups = options
    .map(o => o.selectedValues)
    .filter(vals => vals.length > 0);

  if (valueGroups.length === 0) {
    return previous.length > 0 ? previous : [emptyVariantRow()];
  }

  const combos = cartesian(valueGroups).map(parts => parts.join(" / "));
  const prevByValue = new Map(previous.map(v => [v.value, v]));

  return combos.map(value => {
    const existing = prevByValue.get(value);
    if (existing) {
      return { ...existing, value, title: existing.title || value };
    }
    return emptyVariantRow(value);
  });
}

export function variantsFromProduct(
  productVariants: ProductVariant[] | undefined
): VariantFormRow[] {
  if (!productVariants?.length) {
    return [emptyVariantRow()];
  }
  return productVariants.map(v => {
    const value = v.value ?? v.weight ?? "";
    const stock = v.total_stock ?? v.available_stock ?? v.stock;
    const sku = v.sku ?? "";
    return {
      key: newKey(),
      value,
      title: v.title ?? value,
      sku,
      originalSku: sku,
      price: v.price ?? "",
      discounted_price: v.discounted_price ?? "",
      tax: v.tax ?? "",
      total_stock: stock != null ? String(stock) : "",
      is_active: v.is_active !== false,
    };
  });
}

export function toVariantCreateItems(
  rows: VariantFormRow[],
  options?: { isEdit?: boolean }
): ProductVariantCreateItem[] {
  const isEdit = options?.isEdit === true;
  return rows
    .filter(r => r.value.trim())
    .map(r => {
      const stock = Number(r.total_stock);
      const price = Number(r.price).toFixed(2);
      const display_price = resolveDisplayPriceAmount(r);
      const item: ProductVariantCreateItem = {
        value: r.value.trim(),
        price,
        display_price,
        tax: Number(r.tax).toFixed(2),
        total_stock: stock,
        reserved_stock: 0,
        available_stock: stock,
        is_active: r.is_active !== false,
      };
      const sku = r.sku.trim();
      const originalSku = (r.originalSku ?? "").trim();
      // Create: include optional sku when provided.
      // Edit: only include sku when the user actually changed it.
      if (isEdit) {
        if (sku && sku !== originalSku) {
          item.sku = sku;
        }
      } else if (sku) {
        item.sku = sku;
      }
      const discounted = Number(r.discounted_price);
      if (Number.isFinite(discounted) && discounted > 0) {
        item.discounted_price = discounted.toFixed(2);
      }
      return item;
    });
}

export function resolveVariantMasterId(
  options: ProductOptionRow[]
): number | undefined {
  const withMaster = options.find(o => o.masterId != null);
  return withMaster?.masterId ?? undefined;
}

function ValuesMultiSelect({
  availableValues,
  selectedValues,
  onChange,
  disabled,
}: {
  availableValues: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || availableValues.length === 0}
            className="bg-muted/40 hover:bg-muted/60 h-10 w-full justify-start rounded-xl border-transparent font-normal text-muted-foreground"
          >
            {selectedValues.length === 0
              ? "Select values"
              : `${selectedValues.length} selected`}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-2" align="start">
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {availableValues.map(val => {
              const checked = selectedValues.includes(val);
              return (
                <label
                  key={val}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted",
                    checked && "bg-muted"
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(val)}
                  />
                  <span>{val}</span>
                </label>
              );
            })}
            {availableValues.length === 0 && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                Select a title first.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedValues.map(val => (
            <Badge
              key={val}
              variant="secondary"
              className="gap-1 rounded-full pr-1 font-normal"
            >
              {val}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-background"
                onClick={() => onChange(selectedValues.filter(v => v !== val))}
                aria-label={`Remove ${val}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductFormVariants({
  variantMasters,
  options,
  onOptionsChange,
  variants,
  onVariantsChange,
}: ProductFormVariantsProps) {
  const masterOptions = useMemo(
    () =>
      variantMasters.map(m => ({
        value: String(m.id),
        label: m.title,
      })),
    [variantMasters]
  );

  const setOptionsAndRebuild = (nextOptions: ProductOptionRow[]) => {
    onOptionsChange(nextOptions);
    onVariantsChange(buildVariantsFromOptions(nextOptions, variants));
  };

  const updateOption = (key: string, patch: Partial<ProductOptionRow>) => {
    setOptionsAndRebuild(
      options.map(o => (o.key === key ? { ...o, ...patch } : o))
    );
  };

  const updateVariant = (key: string, patch: Partial<VariantFormRow>) => {
    onVariantsChange(
      variants.map(v => (v.key === key ? { ...v, ...patch } : v))
    );
  };

  const handleAddOption = () => {
    onOptionsChange([...options, emptyOptionRow()]);
  };

  const handleRemoveOption = (key: string) => {
    setOptionsAndRebuild(options.filter(o => o.key !== key));
  };

  const handleMasterSelect = (optionKey: string, masterIdStr: string) => {
    const master = variantMasters.find(m => String(m.id) === masterIdStr);
    updateOption(optionKey, {
      masterId: master?.id ?? null,
      title: master?.title ?? "",
      availableValues: master?.values ?? [],
      selectedValues: [],
    });
  };

  return (
    <div className="bg-muted/20 space-y-5 rounded-2xl border border-border p-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          Variants &amp; Pricing
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Define options, then set price, tax, and stock for each variant.
        </p>
      </div>

      {/* Product options */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-medium">Product options</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAddOption}
            className="border-primary/30 hover:bg-primary/5 h-8 gap-1 rounded-lg text-primary"
          >
            <Plus className="h-3.5 w-3.5" /> Add option
          </Button>
        </div>

        {options.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No options yet. Click &quot;Add option&quot; to choose a variant
            master (e.g. Weight, Size).
          </p>
        )}

        <div className="space-y-3">
          {options.map(opt => (
            <div
              key={opt.key}
              className="border-border/60 grid gap-3 rounded-xl border bg-background p-3 sm:grid-cols-[1fr_1.4fr_auto]"
            >
              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">
                  Title
                </Label>
                <SearchableSelect
                  value={opt.masterId ? String(opt.masterId) : ""}
                  onValueChange={v => handleMasterSelect(opt.key, v)}
                  options={masterOptions}
                  placeholder="Select option..."
                  emptyText="No variant masters found."
                  seedOption={
                    opt.masterId
                      ? { value: String(opt.masterId), label: opt.title }
                      : undefined
                  }
                />
              </div>
              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">
                  Values
                </Label>
                <ValuesMultiSelect
                  availableValues={opt.availableValues}
                  selectedValues={opt.selectedValues}
                  onChange={vals =>
                    updateOption(opt.key, { selectedValues: vals })
                  }
                  disabled={!opt.masterId}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-10 w-10 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveOption(opt.key)}
                  title="Remove option"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Variants table */}
      <div className="space-y-2">
        <p className="text-[11px] text-muted-foreground">
          * Tax (%) is included in the entered price.
        </p>
        <div className="overflow-x-auto rounded-xl border border-border bg-background">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[100px]">variant</TableHead>
                <TableHead className="min-w-[120px]">Title</TableHead>
                <TableHead className="min-w-[100px]">SKU</TableHead>
                <TableHead className="min-w-[110px]">
                  Price INR <span className="text-destructive">*</span>
                </TableHead>
                <TableHead className="min-w-[120px]">
                  Discounted Price
                </TableHead>
                <TableHead className="min-w-[100px]">Display Price</TableHead>
                <TableHead className="min-w-[90px]">
                  Tax (%) <span className="text-destructive">*</span>
                </TableHead>
                <TableHead className="min-w-[80px]">
                  Stock <span className="text-destructive">*</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variants.map(row => (
                <TableRow key={row.key}>
                  <TableCell className="align-middle text-xs font-medium text-muted-foreground">
                    {row.value || "—"}
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.title}
                      onChange={e =>
                        updateVariant(row.key, { title: e.target.value })
                      }
                      placeholder="Title"
                      className="bg-muted/40 h-9 rounded-lg border-transparent"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.sku}
                      onChange={e =>
                        updateVariant(row.key, { sku: e.target.value })
                      }
                      placeholder="SKU"
                      className="bg-muted/40 h-9 rounded-lg border-transparent"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        ₹
                      </span>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.price}
                        onChange={e =>
                          updateVariant(row.key, { price: e.target.value })
                        }
                        placeholder="0.00"
                        required
                        className="bg-muted/40 h-9 rounded-lg border-transparent pl-6"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        ₹
                      </span>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.discounted_price}
                        onChange={e =>
                          updateVariant(row.key, {
                            discounted_price: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        className="bg-muted/40 h-9 rounded-lg border-transparent pl-6"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="align-middle text-sm tabular-nums text-muted-foreground">
                    {displayPrice(row)}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.tax}
                      onChange={e =>
                        updateVariant(row.key, { tax: e.target.value })
                      }
                      placeholder="Tax"
                      required
                      className="bg-muted/40 h-9 rounded-lg border-transparent"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={row.total_stock}
                      onChange={e =>
                        updateVariant(row.key, {
                          total_stock: e.target.value,
                        })
                      }
                      placeholder="0"
                      required
                      className="bg-muted/40 h-9 rounded-lg border-transparent"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
